import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messagingApi } from '@/api/messaging';
import { useSessionStore } from '@/state/sessionStore';

/** Query keys for what a neighbor sent as broadcasts, and their saved lists of neighbors. */
export const broadcastKeys = {
  all: ['broadcasts'] as const,
  detail: (broadcastId: string) => ['broadcasts', broadcastId] as const,
  lists: ['broadcast-lists'] as const,
};

function useSignedIn() {
  return useSessionStore((s) => s.status === 'signedIn');
}

/** What I sent, newest first, with how many it reached and how many read it. */
export function useBroadcasts() {
  const signedIn = useSignedIn();
  return useQuery({ queryKey: broadcastKeys.all, queryFn: messagingApi.listBroadcasts, enabled: signedIn });
}

export function useBroadcast(broadcastId: string) {
  const signedIn = useSignedIn();
  return useQuery({
    queryKey: broadcastKeys.detail(broadcastId),
    queryFn: () => messagingApi.getBroadcast(broadcastId),
    enabled: signedIn && Boolean(broadcastId),
  });
}

/** My saved lists of neighbors. */
export function useBroadcastLists() {
  const signedIn = useSignedIn();
  return useQuery({ queryKey: broadcastKeys.lists, queryFn: messagingApi.listBroadcastLists, enabled: signedIn });
}

export function useSaveBroadcastList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, memberIds }: { name: string; memberIds: string[] }) =>
      messagingApi.createBroadcastList(name, memberIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: broadcastKeys.lists }),
  });
}

export function useRemoveBroadcastList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (listId: string) => messagingApi.removeBroadcastList(listId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: broadcastKeys.lists }),
  });
}
